import React, {ReactElement, useState} from 'react';

import numeral from 'numeral';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {styled, useTheme} from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import TableSortLabel from '@mui/material/TableSortLabel';
import useMediaQuery from '@mui/material/useMediaQuery';

import {getComparator, stableSort} from '../../utils/tableHelpers';
import {Order, Align} from '../../constants/table';
import Tooltip from '../CustomTooltip';

import InfoIcon from '../../assets/icons/infosmall.svg';
import ImageWithPlaceholder from '../ImageWithPlaceholder';
import {
  StoreAnalyticsProductType,
  StoreAnalyticsProductDataType
} from '../../api/storesApi';

export type ProductAnalyticsTableType = {
  data: StoreAnalyticsProductDataType;
  loading: boolean;
};

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'rgb(247, 249, 253)'
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const RightAlignedSkeleton = styled(Skeleton)(() => ({
  marginLeft: 'auto'
}));

const placeholderTableBody = () => {
  const mock = [1, 2, 3];
  return mock.map(
    (row: number): ReactElement => (
      <StyledTableRow
        key={row}
        sx={{'&:last-child td, &:last-child th': {border: 0}}}>
        <TableCell component="th" scope="row">
          <Skeleton variant="rectangular" width={50} height={50} />
        </TableCell>
        <TableCell component="th" scope="row">
          <Skeleton variant="rectangular" width={300} />
        </TableCell>
        <TableCell align="right">
          <RightAlignedSkeleton variant="rectangular" width={100} />
        </TableCell>
        <TableCell align="right">
          <RightAlignedSkeleton variant="rectangular" width={100} />
        </TableCell>
        <TableCell align="right">
          <RightAlignedSkeleton variant="rectangular" width={100} />
        </TableCell>
        <TableCell align="right">
          <RightAlignedSkeleton variant="rectangular" width={100} />
        </TableCell>
      </StyledTableRow>
    )
  );
};

interface AnalyticsTableHeadCell {
  id: keyof StoreAnalyticsProductType;
  label: string;
  numeric?: boolean;
  align?: Align;
}

const headCells: readonly AnalyticsTableHeadCell[] = [
  {
    id: 'image',
    label: null
  },
  {
    id: 'name',
    numeric: true,
    label: 'Product'
  },
  {
    id: 'price',
    numeric: true,
    label: 'Price',
    align: 'right'
  },
  {
    id: 'quantity',
    numeric: true,
    label: 'Items Sold',
    align: 'right'
  },
  {
    id: 'sales',
    numeric: true,
    label: 'Gross Sales',
    align: 'right'
  },
  {
    id: 'daysInStore',
    numeric: true,
    label: 'Time in Store',
    align: 'right'
  }
];

const profitHeadCells: readonly AnalyticsTableHeadCell[] = [
  {
    id: 'image',
    label: null
  },
  {
    id: 'name',
    numeric: true,
    label: 'Product'
  },
  {
    id: 'price',
    numeric: true,
    label: 'Price',
    align: 'right'
  },
  {
    id: 'margin',
    numeric: true,
    label: 'Current Margin',
    align: 'right'
  },
  {
    id: 'quantity',
    numeric: true,
    label: 'Items Sold',
    align: 'right'
  },
  {
    id: 'sales',
    numeric: true,
    label: 'Gross Sales',
    align: 'right'
  },
  {
    id: 'profit',
    numeric: true,
    label: 'Est. Profit',
    align: 'right'
  },
  {
    id: 'daysInStore',
    numeric: true,
    label: 'Time in Store',
    align: 'right'
  }
];

const ProductAnalyticsTable = ({
  data,
  loading
}: ProductAnalyticsTableType): ReactElement => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof StoreAnalyticsProductType>('sales');

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof StoreAnalyticsProductType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler =
    (property: keyof StoreAnalyticsProductType) =>
    (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const productTableEmptyState = () => {
    return (
      !loading && (
        <div className="admin-ManageStores-emptyState">
          <h3>No products sold in this time duration</h3>
        </div>
      )
    );
  };

  const renderProductTableBody = () => {
    if (loading || !data) {
      return placeholderTableBody();
    }
    if (data?.products?.length) {
      return stableSort(data.products, getComparator(order, orderBy)).map(
        (row: StoreAnalyticsProductType): ReactElement => (
          <StyledTableRow
            key={row.image}
            sx={{'&:last-child td, &:last-child th': {border: 0}}}>
            <TableCell component="th" scope="row">
              <ImageWithPlaceholder
                classes="admin-ProductAnalyticsTable-image"
                src={row.image}
                width={50}
                height={53}
              />
            </TableCell>
            <TableCell component="th" scope="row">
              {row.name}
            </TableCell>
            <TableCell align="right">
              {numeral(row.price).format('$0,0.00')}
            </TableCell>
            {Object.prototype.hasOwnProperty.call(row, 'margin') && (
              <TableCell align="right">
                {numeral(row?.margin).format('$0,0.00')}
              </TableCell>
            )}
            <TableCell align="right">{row.quantity}</TableCell>
            <TableCell align="right">
              {numeral(row.sales).format('$0,0.00')}
            </TableCell>
            {Object.prototype.hasOwnProperty.call(row, 'profit') && (
              <TableCell align="right">
                {numeral(row.profit).format('$0,0.00')}
              </TableCell>
            )}
            <TableCell align="right">{`${row.daysInStore} days`}</TableCell>
          </StyledTableRow>
        )
      );
    }
    return productTableEmptyState();
  };

  const showProfitHeaders =
    !loading &&
    data?.products?.length &&
    Object.prototype.hasOwnProperty.call(data?.products[0], 'margin');
  const visibleHeadCells = showProfitHeaders ? profitHeadCells : headCells;

  return (
    <div className="admin-ProductAnalyticsTable">
      <div className="admin-ProductAnalyticsTable-header">
        <h3 className="admin-ProductAnalyticsTable-title">
          Product Performance
          <Tooltip
            title="Price and Gross Sales exclude shipping and tax"
            arrow
            placement="bottom-start">
            <div className="info-icon">
              <img src={InfoIcon} alt="Info" />
            </div>
          </Tooltip>
        </h3>
      </div>
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: 'calc(100vw - 200px)',
          maxHeight: mobileView ? 'calc(100vh - 200px)' : undefined
        }}>
        <Table sx={{minWidth: 750}} stickyHeader>
          <TableHead>
            <TableRow>
              {visibleHeadCells.map(headCell => (
                <TableCell
                  key={headCell.id}
                  align={headCell.align}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    color: 'rgb(68, 68, 68)',
                    fontWeight: 'bold',
                    fontFamily:
                      'SharpSans, Helvetica, Arial, "Trebuchet MS", sans-serif',
                    lineHeight: 1.2,
                    fontSize: '12.6px',
                    backgroundColor: 'rgb(237, 237, 237)',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}>
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{renderProductTableBody()}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ProductAnalyticsTable;
